import { z } from 'zod';
import { BuyerResearchStageWithNonActive } from './BuyerResearchStage';
import { TopLevelRegion } from './TopLevelRegion';
export var SegmentState;
(function (SegmentState) {
    SegmentState["UPLOADED_TO_BEESWAX"] = "uploaded_to_beeswax";
    SegmentState["UPLOADED_TO_S3"] = "uploaded_to_s3";
    SegmentState["PROCESSING"] = "processing";
    SegmentState["ERROR"] = "error";
    SegmentState["PROCESSED"] = "processed";
    SegmentState["WAITING_FOR_PROCESSING"] = "waiting_for_processing";
    SegmentState["STATE_UNSPECIFIED"] = "unspecified";
})(SegmentState || (SegmentState = {}));
// Hours a non-terminal segment state must persist before an automation is
// considered "stuck / needs attention".
export const STUCK_THRESHOLD_HOURS = 24;
// Terminal / untracked states: reaching one ends the time-in-state clock, so it
// is not recorded as a history entry.
const TERMINAL_SEGMENT_STATES = new Set([
    SegmentState.PROCESSED,
    SegmentState.ERROR,
    SegmentState.STATE_UNSPECIFIED,
]);
export const isTrackedSegmentState = (state) => state != null && !TERMINAL_SEGMENT_STATES.has(state);
const SEGMENT_STATE_HISTORY_KEYS = [
    'ipAddress',
    'userId',
    'maid',
];
// Merge incoming state changes into the per-type history. Appends
// { state, enteredAt: now } when a type's state actually changes to a
// tracked (non-terminal) state, or when the state is unchanged but the last
// recorded history entry for that type is missing or doesn't match — this
// seeds files that were already sitting in a non-terminal state before
// history tracking existed (or whose history is otherwise stale), since the
// hourly poll keeps rewriting them in place with the same state. Terminal
// transitions append nothing. Types absent from nextStates (not being
// written) are skipped. Returns undefined when no entry was added, so
// callers can skip the write.
export const buildSegmentStateHistory = ({ currentHistory, currentStates, nextStates, now, }) => {
    const merged = { ...(currentHistory ?? {}) };
    let changed = false;
    for (const key of SEGMENT_STATE_HISTORY_KEYS) {
        const next = nextStates[key];
        if (next === undefined) {
            continue;
        }
        if (!isTrackedSegmentState(next)) {
            continue;
        }
        const current = currentStates[key];
        const entries = merged[key] ?? [];
        const lastState = entries.length ? entries[entries.length - 1].state : undefined;
        // Append on a real transition, or when the state is unchanged but the
        // history is missing/stale — the latter seeds files the hourly poll keeps
        // rewriting in place, which would otherwise never be recorded.
        if (next !== current || lastState !== next) {
            merged[key] = [...entries, { state: next, enteredAt: now }];
            changed = true;
        }
    }
    return changed ? merged : undefined;
};
export var SegmentType;
(function (SegmentType) {
    SegmentType["SEGMENT_TYPE_UNSPECIFIED"] = "segment_type_unspecified";
    SegmentType["IP"] = "ip";
    SegmentType["USER_ID"] = "user_id";
    SegmentType["MAID"] = "maid";
})(SegmentType || (SegmentType = {}));
export var BeeswaxSegmentType;
(function (BeeswaxSegmentType) {
    BeeswaxSegmentType["IP"] = "IP_ADDRESS";
    BeeswaxSegmentType["MAID"] = "OTHER_MOBILE_ID";
    BeeswaxSegmentType["USER_ID"] = "BEESWAX";
})(BeeswaxSegmentType || (BeeswaxSegmentType = {}));
export var IsLiverampDerivedIdl;
(function (IsLiverampDerivedIdl) {
    IsLiverampDerivedIdl["YES"] = "Yes";
    IsLiverampDerivedIdl["NO"] = "No";
})(IsLiverampDerivedIdl || (IsLiverampDerivedIdl = {}));
export const LeadsStrategy = {
    accountsWithLeads: 'Accounts With Leads',
    accountsWithoutLeads: 'Accounts Without Leads',
};
export var LeadsStrategyValue;
(function (LeadsStrategyValue) {
    LeadsStrategyValue["accountsWithLeads"] = "accountsWithLeads";
    LeadsStrategyValue["accountsWithoutLeads"] = "accountsWithoutLeads";
})(LeadsStrategyValue || (LeadsStrategyValue = {}));
export var FiltersRelationship;
(function (FiltersRelationship) {
    FiltersRelationship["and"] = "and";
    FiltersRelationship["or"] = "or";
})(FiltersRelationship || (FiltersRelationship = {}));
export var DSPProvider;
(function (DSPProvider) {
    DSPProvider["beeswax"] = "beeswax";
    DSPProvider["xandr"] = "xandr";
    DSPProvider["tradedesk"] = "tradedesk";
    DSPProvider["innovid"] = "innovid";
    DSPProvider["manual"] = "manual";
})(DSPProvider || (DSPProvider = {}));
export const dspProviderOptions = Object.entries(DSPProvider).map(([key, label]) => ({
    value: key,
    label: label[0].toUpperCase() + label.slice(1),
}));
export const automatedProgrammaticSegmentDspProviders = [
    DSPProvider.beeswax,
    DSPProvider.tradedesk,
];
export const automatedProgrammaticSegmentDspProviderOptions = automatedProgrammaticSegmentDspProviders.map((value) => ({
    value,
    label: value[0].toUpperCase() + value.slice(1),
}));
export const createAutomatedProgrammaticSegmentSchema = z
    .object({
    tag: z.string().min(3),
    campaignId: z.number(),
    region: z.nativeEnum(TopLevelRegion),
    hasIspData: z.boolean().default(false),
    segmentType: z.nativeEnum(SegmentType),
    buyerResearchStages: z
        .enum(BuyerResearchStageWithNonActive, {
        error: 'Please select BRS stage',
    })
        .array(),
    leadsStrategy: z.nativeEnum(LeadsStrategyValue).optional(),
    excludedAccounts: z.array(z.string()).optional(),
    filtersRelationship: z.nativeEnum(FiltersRelationship).optional(),
    targetPersonas: z.array(z.object({
        id: z.number(),
        name: z.string(),
    })),
    dspProvider: z.union([
        z.literal(DSPProvider.beeswax),
        z.literal(DSPProvider.tradedesk),
    ]),
    tradedeskAdvertiser: z.string().optional(),
})
    .superRefine((data, ctx) => {
    if (data.dspProvider === DSPProvider.tradedesk &&
        !data.tradedeskAdvertiser) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['tradedeskAdvertiser'],
            message: 'Tradedesk advertiser is required',
        });
    }
    if (data.dspProvider === DSPProvider.tradedesk &&
        data.segmentType === SegmentType.USER_ID) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['segmentType'],
            message: 'Tradedesk does not support User IDs',
        });
    }
});
export const leadsStrategyOptions = Object.entries(LeadsStrategy).map(([key, label]) => ({
    value: key,
    label,
}));
export const filtersRelationshipOptions = Object.entries(FiltersRelationship).map(([key, label]) => ({
    value: key,
    label: label.toLocaleUpperCase(),
}));
export var ForecastState;
(function (ForecastState) {
    ForecastState["SCHEDULED"] = "scheduled";
    ForecastState["PROCESSING"] = "processing";
    ForecastState["ERROR"] = "error";
    ForecastState["PROCESSED"] = "processed";
})(ForecastState || (ForecastState = {}));
//# sourceMappingURL=ProgrammaticDomains.js.map